import { useMemo, useState } from 'react'
import type { Navigate } from '../App'
import {
  COVERAGE_PHRASES,
  HIGHLIGHT_TARGETS,
  TOP_CHARS_ZH,
  type CoveragePhrase,
} from '../data/topWordsZh'
import { markWords } from '../lib/highlight'
import { prettyPinyin } from '../lib/pinyin'
import { SpeakButton } from '../components/SpeakButton'

/**
 * The commonest hundred words, seen at work.
 *
 * Not a hundred rows. The words are the *target*; the phrases are the content,
 * each one carrying several of them at once the way real sentences do, with the
 * tracked words marked inside the Chinese. A column of glosses is exactly what
 * this method exists to argue against — knowing that 把 means "to take" has
 * never once helped anybody use 把.
 *
 * Deliberately outside the island system: no SRS, no scoring, no schedule.
 * Feeding a hundred words into the review queue would bury the six or seven
 * islands that are the actual product. This is something you dip into when you
 * feel rusty.
 */
export function RefresherView({ navigate }: { navigate: Navigate }) {
  const [mode, setMode] = useState<'phrases' | 'words'>('phrases')
  const [showEnglish, setShowEnglish] = useState(true)
  const [query, setQuery] = useState('')

  // Characters are the target; the words are here so 什么 draws as one unit.
  const targets = HIGHLIGHT_TARGETS

  const phrases = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COVERAGE_PHRASES
    return COVERAGE_PHRASES.filter(
      (p) =>
        p.hanzi.includes(q) ||
        p.pinyin.toLowerCase().includes(q) ||
        p.english.toLowerCase().includes(q),
    )
  }, [query])

  const covered = useMemo(() => {
    const seen = new Set<string>()
    for (const p of COVERAGE_PHRASES) for (const w of p.covers) seen.add(w)
    return seen.size
  }, [])

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Refresher</div>
        <h1 className="page-title">
          <span className="page-title-zh">最常用的一百个汉字</span>
        </h1>
        <p className="lede">
          The hundred characters you meet most often, shown inside phrases you could actually say
          rather than in a column. They are <span className="freq-word">marked like this</span>,
          and where several form a word it is marked as the word. Nothing here is drilled or
          scheduled — it is a refresher, not another island.
        </p>
      </header>

      <div className="row" style={{ marginBottom: '1rem', gap: '0.6rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn ${mode === 'phrases' ? 'is-primary' : ''}`}
          onClick={() => setMode('phrases')}
        >
          {COVERAGE_PHRASES.length} phrases
        </button>
        <button
          type="button"
          className={`btn ${mode === 'words' ? 'is-primary' : ''}`}
          onClick={() => setMode('words')}
        >
          The hundred 汉字
        </button>
        <button
          type="button"
          className="btn is-quiet is-small"
          onClick={() => setShowEnglish((v) => !v)}
        >
          {showEnglish ? 'Hide English' : 'Show English'}
        </button>
      </div>

      {mode === 'phrases' ? (
        <>
          <label className="field" style={{ marginBottom: '1rem' }}>
            <span className="field-label">Find a phrase</span>
            <input
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="时候, shi hou, when…"
            />
          </label>

          {phrases.length === 0 ? (
            <div className="empty">Nothing matches “{query}”.</div>
          ) : (
            phrases.map((phrase) => (
              <PhraseCard
                key={phrase.hanzi}
                phrase={phrase}
                targets={targets}
                showEnglish={showEnglish}
              />
            ))
          )}
        </>
      ) : (
        <WordIndex showEnglish={showEnglish} />
      )}

      <p className="muted" style={{ fontSize: '0.84rem', marginTop: '1.4rem' }}>
        {COVERAGE_PHRASES.length} phrases covering {covered} of {TOP_CHARS_ZH.length} characters.
        Frequency order follows Jun Da's Modern Chinese Character Frequency List.
      </p>

      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate({ view: 'dashboard' })}
        style={{ marginTop: '0.8rem' }}
      >
        ← Today
      </button>
    </>
  )
}

/**
 * One phrase: 汉字, tone-marked pinyin, English, audio.
 *
 * The pinyin goes through prettyPinyin rather than being printed as written, so
 * the tone diacritics are guaranteed — the same ǎ / ì markers the islands use.
 * If content ever arrives with numeric tones ("wo3 hao3") it is normalised to
 * "wǒ hǎo" for display instead of leaking the raw form onto the screen.
 */
function PhraseCard({
  phrase,
  targets,
  showEnglish,
}: {
  phrase: CoveragePhrase
  targets: string[]
  showEnglish: boolean
}) {
  const segments = useMemo(() => markWords(phrase.hanzi, targets), [phrase.hanzi, targets])
  const pinyin = useMemo(() => prettyPinyin(phrase.pinyin), [phrase.pinyin])

  return (
    <div className="card">
      <div className="hanzi" style={{ fontSize: '1.6rem', lineHeight: 1.7 }}>
        {segments.map((seg, i) =>
          seg.marked ? (
            <span key={i} className="freq-word">
              {seg.text}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </div>

      <div className="data phrase-pinyin">{pinyin}</div>

      {showEnglish && (
        <p className="english" style={{ marginTop: '0.5rem' }}>
          {phrase.english}
        </p>
      )}

      {phrase.note ? (
        <p className="muted" style={{ fontSize: '0.86rem', marginTop: '0.6rem' }}>
          {phrase.note}
        </p>
      ) : null}

      <div className="row" style={{ marginTop: '0.9rem', gap: '0.6rem', flexWrap: 'wrap' }}>
        <SpeakButton
          text={phrase.hanzi}
          language="zh"
          label="Play"
          className="btn is-small"
          withSlow
        />
      </div>
    </div>
  )
}

/**
 * The hundred themselves, each with its own audio.
 *
 * A single character is exactly where pronunciation goes wrong and exactly what
 * a phrase-level Play button cannot isolate — 是 inside a sentence goes by too
 * fast to check your tone against.
 */
function WordIndex({ showEnglish }: { showEnglish: boolean }) {
  return (
    <div className="word-index">
      {TOP_CHARS_ZH.map((word) => (
        <div key={word.rank} className="word-row">
          <span className="word-rank">{word.rank}</span>
          <span className="hanzi word-hanzi">{word.hanzi}</span>
          <span className="data word-pinyin">{prettyPinyin(word.pinyin)}</span>
          <span className="word-pos">{word.pos ?? ''}</span>
          <span className="word-english">{showEnglish ? word.english : ''}</span>
          <SpeakButton
            text={word.hanzi}
            language="zh"
            label="Play"
            className="btn is-small is-quiet"
            withSlow
          />
        </div>
      ))}
    </div>
  )
}
